import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { FormComponent, INPUT_MASKS, INPUT_PLACEHOLDERS, SharedFormOptions } from '@project/shared';
import { DoctorInviteManagerService, EDoctorsModuleAction } from '../doctor-invite-manager.service';
import { DoctorInviteUtils, IDoctorInviteFormModel } from '../doctor-invite.common';
import { fillWithZeros, LibValidators } from '@core/helpers';
import { NotificationsService } from '@lib/notifications';
import { ISelectOption } from '@lib/ui/form-elements';
import { TranslateService } from '@project/translate';
import {
  IDoctorToInvite,
  IDoctor,
  IState,
  License,
  EDoctorSpecializationDTO,
  CdpProfessionValidateCrmResponse,
  SusUser,
  EGenderDTO,
} from '@project/view-models';
import {
  DoctorSpecializationDataProvider,
  AddressesApiProviderService,
  DoctorsApiProviderService,
  CnsApiProviderService,
} from '@project/data-providers';
import { Cns } from 'src/app/core/helpers/utils/cns';
import { HcService } from '@project/services';

@Component({
  selector: 'app-doctor-invite-details-form',
  templateUrl: './doctor-invite-details-form.component.html',
  styleUrls: ['./doctor-invite-details-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DoctorInviteDetailsFormComponent extends FormComponent implements OnInit {
  @Input() doctor: IDoctor;
  @Input() isPending = false;

  @Output() dataSubmit = new EventEmitter<IDoctorToInvite>();

  public states$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public licenseText = new BehaviorSubject<string>(null);
  public isLoadingDoctorDataByCNS = false;
  public isValidatingCns = false;

  public form: FormGroup;
  public doctorSpecializationOptions: ISelectOption[] = this.doctorSpecializationDataProvider.getSelectOptionsByCode();

  public readonly genderOptions: ISelectOption[] = SharedFormOptions.genderOptions();
  public readonly dateInputMask = INPUT_MASKS.date;
  public readonly dateInputPlaceholder = INPUT_PLACEHOLDERS.date;
  public readonly tagFilter$ = this.doctorInviteManagerService.tagFilter$;
  public readonly isPublicHC = this.hcService.getHcConfig().isPublic;

  private readonly requiredValidation = LibValidators.required(TranslateService.localize('validations.required'));
  private readonly numberOnlyValidation = LibValidators.numberOnly(
    TranslateService.localize('validations.should-be-integer'),
  );

  get licenses() {
    return this.form.controls.licenses as FormArray;
  }

  constructor(
    private fb: FormBuilder,
    private doctorSpecializationDataProvider: DoctorSpecializationDataProvider,
    private addressesApiProviderService: AddressesApiProviderService,
    private cnsApiProviderService: CnsApiProviderService,
    private doctorsApiProviderService: DoctorsApiProviderService,
    private doctorInviteManagerService: DoctorInviteManagerService,
    private notificationService: NotificationsService,
    private hcService: HcService,
  ) {
    super();

    const isPublicHC = this.hcService.getHcConfig().isPublic;
    const formDeclaration = isPublicHC
      ? DoctorInviteUtils.getPublicHcDoctorInviteFromDeclaration(this.fb)
      : DoctorInviteUtils.getPrivateHcDoctorInviteFromDeclaration(this.fb);
    this.form = this.fb.group(formDeclaration);
  }

  protected onFormInit() {
    const brazilCountryId = '02226fa0-b0ab-418d-9ec1-d5a0856c37cc';
    this.loadStateListByCountryId(brazilCountryId);

    const isEditMode = this.doctor != null;
    if (isEditMode) this.initFormToEditDoctor(this.doctor);
    else this.initFormToInviteDoctor();

    this.setupCRMListener();
  }

  private initFormToEditDoctor(doctor: IDoctor) {
    this.doctorSpecializationOptions = this.doctorSpecializationDataProvider.getSelectOptionsByCode(
      false,
      doctor.specialization,
    );

    this.form.patchValue({
      specialization: doctor.specialization || '',
      firstName: doctor.firstName || '',
      lastName: doctor.lastName || '',
      phone: doctor.phone || '',
      gender: doctor.gender || this.genderOptions[0].value,
      email: doctor.email || '',
      cns: doctor.cns || '',
      cpf: doctor.cpf || '',
    });

    this.setupCNSListenerForValidation();

    const doctorHasBirthDate = this.doctor.birthDate != null;
    if (doctorHasBirthDate) {
      const formattedBirthDate = this.formatFromIsoDateToDisplay(this.doctor.birthDate);
      this.form.patchValue({ dateOfBirth: formattedBirthDate });
    }

    const doctorHasLicense = this.doctor.licenses.length > 0;
    if (doctorHasLicense) {
      this.licenses.clear();
      this.doctor.licenses.forEach((licenseData) => {
        const licenseGroup = this.fb.group({
          state_id: [licenseData.state_id, [this.requiredValidation]],
          license: [licenseData.license, [this.requiredValidation, this.numberOnlyValidation]],
        });

        this.licenses.push(licenseGroup);
      });
    }
  }

  private initFormToInviteDoctor() {
    this.setupDoctorDataAutofillByCNS();
    this.form.patchValue({ gender: this.genderOptions[0].value });
  }

  protected doCleanSubmit() {
    this.doctorInviteManagerService.dispatchAction(EDoctorsModuleAction.SetTagSelected);
    this.form.patchValue({ tag: this.tagFilter$.value });

    const userData = DoctorInviteUtils.createDoctorInviteDto(this.form.value as IDoctorInviteFormModel);
    this.dataSubmit.emit(userData);

    const isCreateMode = this.doctor == null;
    if (isCreateMode) this.onSubmitInviteDoctor();
  }

  private onSubmitInviteDoctor() {
    const { gender, specialization } = this.form.value;

    this.form.reset();
    this.form.patchValue({ gender, specialization });

    this.doctorInviteManagerService.dispatchAction(EDoctorsModuleAction.ClearTagSelected);
  }

  public setLicenceToValidate(license) {
    this.licenseText.next(license);
  }

  public removeLicense(licenseIdx: number) {
    if (this.licenses.length <= 1) return;
    this.licenses.removeAt(licenseIdx);
  }

  public addLicense(num: number) {
    const licenseField = this.fb.group({
      state_id: ['', [this.requiredValidation]],
      license: ['', [this.requiredValidation, this.numberOnlyValidation]],
    });

    for (let i = -1; i < num; i++) this.licenses.push(licenseField);
  }

  protected resetFieldsLicense() {
    this.licenses.value.forEach((item) => {
      if (this.licenses.value.indexOf(item) > 0) {
        this.licenses.removeAt(this.licenses.value.indexOf(item));
      }
    });
  }

  private cleanLicense(licenses: AbstractControl[]) {
    licenses.forEach((item) => {
      item.patchValue({ state_id: '', license: '' });
      item.get('state_id').setErrors(null);
      item.get('license').setErrors(null);
    });
  }

  private getFilterLicense(licenseValue: string): License {
    return this.licenses.value.find((license: License) => license.license === licenseValue);
  }

  private validateField(controls: AbstractControl[], license: string) {
    controls.forEach((item) => {
      if (item.get('license').value === license) {
        item.get('license').setErrors({ invalid: TranslateService.localize('validations.license-invalid') });
      }
    });
  }

  private validateCRM(licenseValue: string) {
    if (this.form.get('specialization').value === '') {
      this.form.get('specialization').setErrors({ invalid: TranslateService.localize('validations.required') });
    } else if (!this.isPsychologistSpecialization(this.form.get('specialization').value)) {
      const license = this.getFilterLicense(licenseValue);

      if (license?.license.length > 0 && license?.state_id.length > 0) {
        this.doctorsApiProviderService
          .validateLicense(license)
          .subscribe((cdpProfessionValidateCrmResponse: CdpProfessionValidateCrmResponse) => {
            if (cdpProfessionValidateCrmResponse.professions[0].error !== '') {
              this.validateField(this.licenses.controls, license.license);
            } else if (cdpProfessionValidateCrmResponse.professions[0].valid === false) {
              this.validateField(this.licenses.controls, license.license);
            }
          });
      }
    }
  }

  private isPsychologistSpecialization(value: string): boolean {
    return value === EDoctorSpecializationDTO.Psychologist || value === EDoctorSpecializationDTO.ChildPsychologist;
  }

  public changeSpecializationPsychologist(value) {
    if (this.isPsychologistSpecialization(value)) {
      this.cleanLicense(this.licenses.controls);
      this.resetFieldsLicense();
    }
  }

  protected setupCRMListener() {
    this.licenseText.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((license) => {
      if (license != null) this.validateCRM(license);
    });
  }

  private setupCNSListenerForValidation() {
    const cnsControl = this.form.get('cns');

    cnsControl?.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(() => Cns.isValid(cnsControl.value)),
        tap(() => {
          this.setFormDisabledState(true);
          this.isValidatingCns = true;
        }),
        switchMap(() => this.validateCns(cnsControl.value)),
      )
      .subscribe((isValid) => {
        if (!isValid) this.onCnsInvalid();
        this.setFormDisabledState(false);
        this.isValidatingCns = false;
      });
  }

  private setupDoctorDataAutofillByCNS(): void {
    const cnsControl = this.form.get('cns');

    cnsControl?.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(() => Cns.isValid(cnsControl.value)),
        tap(() => {
          this.setFormLoadingState(true);
          this.setFormDisabledState(true);
        }),
        switchMap(() => this.loadDoctorDataByCns(cnsControl.value)),
      )
      .subscribe((user) => {
        if (!user) this.onNotFoundDoctorDataByCNS();
        else this.onFoundDoctorDataByCNS(user);
        this.setFormDisabledState(false);
        this.setFormLoadingState(false);
      });
  }

  private onFoundDoctorDataByCNS(user: SusUser): void {
    this.form.patchValue({
      firstName: user.first_name,
      lastName: user.last_name,
      dateOfBirth: user.birth_date?.trim().replace(/[/-]/g, '.'),
      cpf: user.cpf,
      email: user.email,
      gender: this.translateSUSGenderToGenderDTO(user.gender),
      phone: user.phone?.replace(/[^0-9]/g, ''),
    });
  }

  private onNotFoundDoctorDataByCNS(): void {
    this.notificationService.error({ message: TranslateService.localize('doctors.invite-cns-not-found.warn') });
  }

  private onCnsInvalid(): void {
    this.notificationService.error({ message: TranslateService.localize('doctors.edit-cns-validation.warn') });
  }

  private loadStateListByCountryId(countryId: string) {
    this.addressesApiProviderService.getMyState(countryId).subscribe((states: IState[]) => {
      const select: ISelectOption[] = states.map((x: IState) => ({ value: x.state_id, label: x.name }));
      this.states$.next(select);
    });
  }

  private loadDoctorDataByCns(cns: string): Observable<SusUser | null> {
    return this.cnsApiProviderService.getUserDataByCNS(cns).pipe(
      catchError(() => {
        this.notificationService.error({ message: TranslateService.localize('doctors.invite-cns-search.error') });
        return of(null);
      }),
    );
  }

  private validateCns(cns: string): Observable<boolean> {
    return this.cnsApiProviderService.validateCns(cns).pipe(
      catchError(() => {
        this.notificationService.error({ message: TranslateService.localize('doctors.edit-cns-validation.error') });
        return of(false);
      }),
    );
  }

  private setFormLoadingState(isLoading: boolean): void {
    this.isLoadingDoctorDataByCNS = isLoading;
  }

  private setFormDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.form.disable();
    else this.form.enable();
  }

  private translateSUSGenderToGenderDTO(susGender: string): string {
    switch (susGender) {
      case 'Masculino':
        return EGenderDTO.Male;
      case 'Feminino':
        return EGenderDTO.Female;
      default:
        return this.genderOptions[0].value;
    }
  }

  private formatFromIsoDateToDisplay(date: Date): string {
    return `${fillWithZeros(date.getUTCDate(), 2)}.${fillWithZeros(
      date.getUTCMonth() + 1,
      2,
    )}.${date.getUTCFullYear()}`;
  }
}
